const resources = {
  "en": {
    "debtPayoffPlan": {
      "title": "Debt Payoff Plan",
      "subtitle": "Estimate how long debt payoff will take before you start moving cash toward other goals.",
      "info": {
        "title": "Plan your debt-free date",
        "description": "Enter a monthly payment amount to estimate how long it will take to pay off debts and when your net balance returns to zero."
      },
      "showGoals": "Show goals after debt",
      "sections": {
        "calculator": "Choose a monthly payment",
        "calculatorDescription": "Use one steady monthly payment amount as a planning baseline.",
        "payoffDetails": "Debt payoff details",
        "payoffDetailsDescription": "A simple timeline for clearing the debt balance at the payment level you entered.",
        "netDetails": "Net balance details",
        "netDetailsDescription": "How your cash and debt position move back toward zero while you keep making that payment."
      },
      "form": {
        "monthlyPayment": "Monthly Payment",
        "calculate": "Calculate"
      },
      "notifications": {
        "calculated": "✅ Plan calculated",
        "calculateFailed": "❌ {{error}}",
        "unknownError": "Unknown error"
      },
      "summary": {
        "title": "Debt Payoff Summary",
        "caption": "A quick payoff snapshot based on the payment amount you entered.",
        "totalDebt": "Total Debt: ${{amount}}",
        "monthlyPayment": "Monthly Payment: ${{amount}}",
        "monthsToPayoff": "Months to Payoff: {{count}}",
        "debtFreeDate": "Debt Free Date: {{date}}"
      },
      "net": {
        "title": "Net Worth Outlook",
        "caption": "A simple view of when your cash minus debt balance climbs back to zero.",
        "totalCash": "Total Cash: ${{amount}}",
        "totalDebt": "Total Debt: ${{amount}}",
        "netWorth": "Net Worth: ${{amount}}",
        "monthsToPayoff": "Months to Payoff: {{count}}",
        "netZeroDate": "Net Zero Date: {{date}}"
      },
      "goals": {
        "title": "Goals",
        "description": "If you keep this toggle on, each goal is placed after the debt payoff timeline finishes.",
        "monthsToPayoff": "Months to Payoff: {{count}}"
      },
      "labels": {
        "totalDebt": "Total debt",
        "estimatedTotalDebt": "Estimated debt valuation",
        "monthlyPayment": "Monthly payment",
        "monthsToPayoff": "Months to payoff",
        "debtFreeDate": "Debt-free date",
        "totalCash": "Total cash",
        "estimatedTotalCash": "Estimated cash",
        "netWorth": "Net balance",
        "estimatedNetWorth": "Estimated net balance",
        "monthsToNetZero": "Months to net zero",
        "netZeroDate": "Net-zero date"
      },
      "valuation": {
        "freshTitle": "Cash and net balance use the new valuation model",
        "freshDescription": "This view now estimates cross-currency cash and debt in {{currency}} from native account balances and current FX quotes.",
        "staleTitle": "Cash and net balance are estimated from stale FX quotes",
        "staleDescription": "This view uses the new valuation model in {{currency}}, but some FX quotes are stale. Totals may lag until quotes refresh.",
        "degradedTitle": "Cash and net balance estimate is partially degraded",
        "degradedDescription": "This view uses the new valuation model in {{currency}}, but some FX quotes are missing or failed. Totals may be incomplete until refresh succeeds."
      },
      "rollout": {
        "title": "Explicit balance rollout is active",
        "description": "This debt payoff calculation is using a balance snapshot from the bounded explicit valuation rollout cohort."
      },
      "payoffValuation": {
        "estimatedTitle": "Debt payoff uses explicit debt valuation",
        "estimatedDescription": "This payoff summary values debts and liabilities in {{currency}} from native obligation records and cached FX quotes.",
        "staleTitle": "Debt payoff uses stale FX quotes",
        "staleDescription": "This payoff summary values obligations in {{currency}}, but some FX quotes are stale. The payoff timeline may change after refresh.",
        "degradedTitle": "Debt payoff estimate is partially degraded",
        "degradedDescription": "This payoff summary values obligations in {{currency}}, but some FX quotes are missing or failed. The payoff timeline may be incomplete until refresh succeeds.",
        "partialTitle": "Debt payoff is only partially valued",
        "partialDescription": "This payoff summary values obligations in {{currency}}, but some debts or liabilities cannot be valued with the current quote set."
      }
    }
  },
  "uk": {
    "debtPayoffPlan": {
      "title": "План погашення боргу",
      "subtitle": "Оцініть, скільки триватиме погашення боргів, перш ніж перенаправляти гроші на інші цілі.",
      "info": {
        "title": "Сплануйте дату без боргів",
        "description": "Вкажіть суму щомісячного платежу, щоб оцінити, коли борги будуть закриті і коли чистий баланс вийде в нуль."
      },
      "showGoals": "Показати цілі після погашення боргів",
      "sections": {
        "calculator": "Оберіть щомісячний платіж",
        "calculatorDescription": "Використайте один стабільний щомісячний платіж як базовий сценарій.",
        "payoffDetails": "Деталі погашення боргу",
        "payoffDetailsDescription": "Простий таймлайн закриття боргу для вибраного щомісячного платежу.",
        "netDetails": "Деталі чистого балансу",
        "netDetailsDescription": "Як ваші гроші та борг рухаються назад до нуля, поки ви тримаєте цей платіж."
      },
      "form": {
        "monthlyPayment": "Місячний платіж",
        "calculate": "Розрахувати"
      },
      "notifications": {
        "calculated": "✅ План розраховано",
        "calculateFailed": "❌ {{error}}",
        "unknownError": "Невідома помилка"
      },
      "summary": {
        "title": "Підсумок погашення боргів",
        "caption": "Швидкий знімок плану погашення на основі введеного платежу.",
        "totalDebt": "Загальна сума боргу: ${{amount}}",
        "monthlyPayment": "Місячний платіж: ${{amount}}",
        "monthsToPayoff": "Місяців до погашення: {{count}}",
        "debtFreeDate": "Дата без боргів: {{date}}"
      },
      "net": {
        "title": "Прогноз чистого капіталу",
        "caption": "Простий погляд на те, коли баланс грошей мінус борг повернеться до нуля.",
        "totalCash": "Усього готівки: ${{amount}}",
        "totalDebt": "Загальна сума боргу: ${{amount}}",
        "netWorth": "Чистий капітал: ${{amount}}",
        "monthsToPayoff": "Місяців до погашення: {{count}}",
        "netZeroDate": "Дата виходу в нуль: {{date}}"
      },
      "goals": {
        "title": "Цілі",
        "description": "Якщо цей режим увімкнений, кожна ціль ставиться вже після завершення таймлайну погашення боргу.",
        "monthsToPayoff": "Місяців до виконання: {{count}}"
      },
      "labels": {
        "totalDebt": "Загальний борг",
        "estimatedTotalDebt": "Оціночна вартість боргу",
        "monthlyPayment": "Щомісячний платіж",
        "monthsToPayoff": "Місяців до погашення",
        "debtFreeDate": "Дата без боргів",
        "totalCash": "Усього готівки",
        "estimatedTotalCash": "Оціночна готівка",
        "netWorth": "Чистий баланс",
        "estimatedNetWorth": "Оціночний чистий баланс",
        "monthsToNetZero": "Місяців до нуля",
        "netZeroDate": "Дата виходу в нуль"
      },
      "valuation": {
        "freshTitle": "Готівка і чистий баланс уже працюють на новій моделі оцінки",
        "freshDescription": "Цей розрахунок оцінює мультивалютну готівку та борг у {{currency}} на основі нативних балансів рахунків і актуальних FX-курсів.",
        "staleTitle": "Готівка і чистий баланс оцінені за застарілими FX-курсами",
        "staleDescription": "Цей розрахунок уже використовує нову модель оцінки в {{currency}}, але частина FX-курсів застаріла. Підсумки можуть оновитися після refresh.",
        "degradedTitle": "Оцінка готівки і чистого балансу частково деградована",
        "degradedDescription": "Цей розрахунок уже використовує нову модель оцінки в {{currency}}, але частина FX-курсів відсутня або завершилася помилкою. Підсумки можуть бути неповними до успішного refresh."
      },
      "rollout": {
        "title": "Роллаут явної оцінки балансу вже активний",
        "description": "Цей розрахунок плану погашення використовує balance snapshot із bounded cohort для explicit valuation rollout."
      },
      "payoffValuation": {
        "estimatedTitle": "План погашення вже використовує явну оцінку боргів",
        "estimatedDescription": "Цей підсумок оцінює борги та зобов’язання у {{currency}} на основі нативних записів і кешованих FX-курсів.",
        "staleTitle": "План погашення використовує застарілі FX-курси",
        "staleDescription": "Цей підсумок оцінює зобов’язання у {{currency}}, але частина FX-курсів застаріла. Таймлайн може змінитися після refresh.",
        "degradedTitle": "Оцінка плану погашення частково деградована",
        "degradedDescription": "Цей підсумок оцінює зобов’язання у {{currency}}, але частина FX-курсів відсутня або завершилася помилкою. Таймлайн може бути неповним до успішного refresh.",
        "partialTitle": "План погашення оцінено лише частково",
        "partialDescription": "Цей підсумок оцінює зобов’язання у {{currency}}, але частину боргів або зобов’язань неможливо оцінити з поточним набором курсів."
      }
    }
  }
};

export default resources;
